/**
 * What the player sees when something goes wrong.
 *
 * Before this existed, both failure modes produced the same thing: a black
 * rectangle. A boot error went to `console.error` and stopped; a lost WebGL
 * context left the loop running happily against a renderer that had quietly
 * become a no-op. Neither told anyone anything, and a reviewer looking at a
 * black screen cannot tell "still loading" from "dead" from "my GPU fell over".
 *
 * Deliberately plain: no styling ambitions, no dependency on the renderer that
 * may be the thing that just died, no reliance on anything the failing module
 * exported. It has to work in exactly the conditions where everything else did
 * not.
 */

const OVERLAY_ID = 'rosalie-fatal';

/**
 * Show a full-screen message. Idempotent — a second call replaces the text
 * rather than stacking overlays, because a context loss often arrives as a
 * burst rather than as a single event.
 */
export function showFailure(title: string, detail?: string, canRetry = true): void {
  let overlay = document.getElementById(OVERLAY_ID);
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;
    overlay.setAttribute('role', 'alert');
    overlay.style.cssText = [
      'position:fixed',
      'inset:0',
      'display:flex',
      'flex-direction:column',
      'align-items:center',
      'justify-content:center',
      'gap:0.75rem',
      'padding:2rem',
      'background:#14161a',
      'color:#e6e2dd',
      'font:16px/1.5 system-ui,sans-serif',
      'text-align:center',
      'z-index:9999',
    ].join(';');
    // `document.body` may not exist yet if something fails very early, and a
    // fail-safe that throws is not a fail-safe. `documentElement` is present
    // from the moment parsing starts.
    (document.body ?? document.documentElement)?.appendChild(overlay);
  }

  overlay.textContent = '';

  const heading = document.createElement('p');
  heading.textContent = title;
  heading.style.cssText = 'margin:0;font-size:1.1rem;color:#e08a76';
  overlay.appendChild(heading);

  if (detail) {
    const body = document.createElement('p');
    body.textContent = detail;
    body.style.cssText = 'margin:0;max-width:34rem;opacity:0.75;font-size:0.9rem';
    overlay.appendChild(body);
  }

  if (canRetry) {
    const button = document.createElement('button');
    button.textContent = 'Reload';
    button.style.cssText = [
      'margin-top:0.5rem',
      'padding:0.5rem 1.25rem',
      'font:inherit',
      'color:#14161a',
      'background:#e6e2dd',
      'border:0',
      'border-radius:4px',
      'cursor:pointer',
    ].join(';');
    button.addEventListener('click', () => {
      window.location.reload();
    });
    overlay.appendChild(button);
  }
}

/** Remove the overlay, if one is up. */
export function clearFailure(): void {
  document.getElementById(OVERLAY_ID)?.remove();
}

export interface ContextLossHooks {
  /** Called when the GPU context goes away. Stop simulating here. */
  onLost(): void;
  /** Called if the browser hands a context back. */
  onRestored(): void;
}

/**
 * Handle WebGL context loss.
 *
 * A lost context is not exotic — a driver reset, a laptop switching GPUs, or
 * the browser reclaiming memory under pressure will all do it, and the tab
 * survives. Three installs its own listeners and stops drawing, but the game
 * loop, the physics step and the animation carry on running against a dead
 * renderer, burning CPU and advancing a simulation nobody can see.
 *
 * `preventDefault` on the loss event is REQUIRED: without it the browser will
 * not attempt to restore the context at all, and recovery becomes impossible
 * rather than merely unlikely.
 */
export function installContextLossHandlers(
  canvas: HTMLCanvasElement,
  hooks: ContextLossHooks,
): () => void {
  const onLost = (event: Event): void => {
    event.preventDefault();
    hooks.onLost();
    showFailure(
      'The graphics context was lost.',
      'This usually means the GPU driver reset or the browser reclaimed memory. ' +
        'Reloading should recover it.',
    );
  };
  const onRestored = (): void => {
    hooks.onRestored();
  };

  canvas.addEventListener('webglcontextlost', onLost);
  canvas.addEventListener('webglcontextrestored', onRestored);
  return () => {
    canvas.removeEventListener('webglcontextlost', onLost);
    canvas.removeEventListener('webglcontextrestored', onRestored);
  };
}

/**
 * A pixel-ratio cap that respects a total framebuffer budget.
 *
 * `min(devicePixelRatio, 2)` alone bounds the ratio but not the result: on a
 * large high-DPI display that is still an enormous buffer, and with a 2048²
 * shadow map alongside it that is real GPU memory pressure on the integrated
 * graphics spec §1 targets.
 *
 * The budget is expressed in pixels rather than in a ratio because that is what
 * actually costs something.
 */
export function boundedPixelRatio(
  width: number,
  height: number,
  devicePixelRatio: number,
  maxPixels = 1920 * 1080 * 2,
): number {
  // Degenerate inputs reach here from real places: a canvas measured before
  // layout is 0x0, and `devicePixelRatio` can be NaN in a detached context.
  const dpr = Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1;
  const ratio = Math.min(dpr, 2);
  const area = Math.max(1, width * height) * ratio * ratio;
  if (area <= maxPixels) return ratio;

  const needed = ratio * Math.sqrt(maxPixels / area);
  // The 0.5 floor preserves a minimum quality level, but it must NOT override
  // the budget it exists to serve. Above roughly 4x the budget in raw canvas
  // area, clamping to 0.5 returned a framebuffer twice the size it promised —
  // caught in review, in the one function whose entire job is this number.
  const floored = Math.max(0.5, needed);
  const flooredArea = Math.max(1, width * height) * floored * floored;
  return flooredArea <= maxPixels ? floored : needed;
}
